import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  ButtonType,
  DateInput,
  DropButtonProps,
  Keyboard,
  MaskedInput,
  MaskedInputProps,
  Select,
  Stack,
  TextArea,
  TextInput,
  TextInputProps,
} from 'grommet';
import { Calendar, FormNext, FormPrevious, Hide, View } from 'grommet-icons';
import { Box, BoxProps, Line, Text } from '/src/components';
import { DateString } from '/src/lib/types';
import { pxToRem } from '/src/utils';

export type InputProps = BoxProps & {
  label: string;
  value: string | number | undefined;
  setValue?: (value: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  id?: string;
  name?: string;
  error?: string;
  type?: React.JSX.IntrinsicElements['input']['type'];
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  rows?: number;
  fuseAdminOnly?: boolean;
  hideOptionalText?: boolean;
  hideRequiredMarker?: boolean;
  centerLabel?: boolean;
  disabled?: boolean;
  opacity?: number;
  disabledBackground?: BoxProps['background'];
  componentType?: 'textArea' | 'date' | 'password' | 'percent' | 'percentage';
  autoComplete?: TextInputProps['autoComplete'];
  onSubmit?: (event: React.KeyboardEvent<HTMLElement>) => void;
  mask?: MaskedInputProps['mask'];
  displayValue?: string;
  dateBounds?: [start: DateString, end: DateString];
};

export const Input: React.FC<InputProps> = (props) => {
  const {
    label,
    value = Number.isNaN(props.value) ? '' : props.value,
    setValue,
    id,
    name,
    error,
    placeholder,
    type,
    required,
    minLength,
    maxLength,
    rows,
    fuseAdminOnly,
    opacity,
    hideRequiredMarker,
    centerLabel = true,
    disabled,
    disabledBackground,
    autoComplete,
    componentType,
    onSubmit,
    dateBounds,
    ...boxProps
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [isButtonFocused, setIsButtonFocused] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isActiveLabel = useMemo(
    () =>
      (value !== undefined && value !== null && value !== '') || isFocused || !centerLabel || placeholder || disabled,
    [value, isFocused, centerLabel, placeholder, disabled]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      if (minLength) {
        if (typeof e.target.value === 'number' && (e.target.value as number).toString().length < minLength) return;
        else if (typeof e.target.value === 'string' && (e.target.value as string).length < minLength) return;
      }
      if (maxLength) {
        if (typeof e.target.value === 'number' && (e.target.value as number).toString().length > maxLength) return;
        else if (typeof e.target.value === 'string' && (e.target.value as string).length > maxLength) return;
      }
    }
    setValue?.(e.target.value);
  };

  const renderInput = () => (
    <TextInput
      id={id}
      name={name || id}
      placeholder={placeholder}
      minLength={minLength}
      maxLength={maxLength}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      autoComplete={autoComplete}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      plain
      style={{
        fontFamily: 'Lato, sans-serif',
        height: pxToRem(58),
        paddingTop: pxToRem(26),
        paddingBottom: pxToRem(10),
      }}
      className="hide-input-arrows"
    />
  );

  const renderPercentInput = () => (
    <TextInput
      id={id}
      name={name || id}
      placeholder={placeholder}
      minLength={minLength}
      maxLength={maxLength}
      icon={
        <Text size="20px" color="dark-1" weight={700} style={{ fontFamily: 'Lato, sans-serif' }}>
          %
        </Text>
      }
      reverse
      type="number"
      className="percent-input hide-input-arrows"
      value={value}
      onChange={(e) => setValue?.(e.target.value)}
      required={required}
      disabled={disabled}
      autoComplete={autoComplete}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      plain
      style={{
        fontFamily: 'Lato, sans-serif',
        height: pxToRem(58),
        paddingTop: pxToRem(26),
        paddingBottom: pxToRem(10),
      }}
    />
  );

  const renderDateInput = () => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const yearRange = currentYear - startYear + 1;
    const years = Array.from({ length: yearRange }, (_, i) => (startYear + i).toString());

    const [displayDateValue, setDisplayDateValue] = useState<string>(value ? dayjs(value).format('MM/DD/YYYY') : '');
    const [minDate, maxDate] = dateBounds ?? [];
    const isOutOfBounds = (date: dayjs.Dayjs, unit: dayjs.OpUnitType) =>
      (!!minDate && date.isBefore(minDate, unit)) || (!!maxDate && date.isAfter(maxDate, unit));
    const lockToBounds = (date: dayjs.Dayjs) => {
      if (minDate && date.isBefore(minDate, 'day')) return dayjs(minDate).format('YYYY-MM-DD');
      if (maxDate && date.isAfter(maxDate, 'day')) return dayjs(maxDate).format('YYYY-MM-DD');
      return date.format('YYYY-MM-DD');
    };
    const effectiveValue = lockToBounds(value ? dayjs(value) : dayjs());
    const disabledYears = years.filter((year) => isOutOfBounds(dayjs().year(parseInt(year)), 'year'));
    React.useEffect(() => {
      if (value) {
        setDisplayDateValue(dayjs(value).format('MM/DD/YYYY'));
      } else {
        setDisplayDateValue('');
      }
    }, [value]);

    return (
      <Box direction="row">
        <MaskedInput
          id={id}
          name={name || id}
          value={displayDateValue}
          onChange={(e) => {
            const inputValue = e.target.value;
            setDisplayDateValue(inputValue);

            if (!inputValue) {
              setValue?.('');
              return;
            }

            if (inputValue.length === 10) {
              const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
              if (dateRegex.test(inputValue)) {
                const date = dayjs(inputValue, 'MM/DD/YYYY');
                if (date.isValid()) {
                  setValue?.(date.format('YYYY-MM-DD'));
                }
              }
            }
          }}
          placeholder={placeholder}
          mask={
            isActiveLabel
              ? [
                  {
                    length: [1, 2],
                    regexp: /^1[0-2]$|^0?[1-9]$|^0$/,
                    placeholder: 'mm',
                  },
                  { fixed: '/' },
                  {
                    length: [1, 2],
                    regexp: /^[1-2][0-9]$|^3[0-1]$|^0?[1-9]$|^0$/,
                    placeholder: 'dd',
                  },
                  { fixed: '/' },
                  {
                    length: 4,
                    regexp: /^[1-2]$|^19$|^20$|^19[0-9]$|^20[0-9]$|^19[0-9][0-9]$|^20[0-9][0-9]$/,
                    placeholder: 'yyyy',
                  },
                ]
              : undefined
          }
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            const inputValue = (e.target as HTMLInputElement).value;
            if (inputValue && inputValue.length === 10) {
              const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
              if (dateRegex.test(inputValue)) {
                const date = dayjs(inputValue, 'MM/DD/YYYY');
                if (date.isValid()) {
                  setValue?.(date.format('YYYY-MM-DD'));
                  setDisplayDateValue(date.format('MM/DD/YYYY'));
                } else {
                  setDisplayDateValue(value ? dayjs(value).format('MM/DD/YYYY') : '');
                }
              } else {
                setDisplayDateValue(value ? dayjs(value).format('MM/DD/YYYY') : '');
              }
            } else if (inputValue && inputValue.length < 10) {
              setDisplayDateValue(value ? dayjs(value).format('MM/DD/YYYY') : '');
            }
          }}
          reverse
          plain
          style={{
            fontFamily: 'Lato, sans-serif',
            height: pxToRem(58),
            paddingTop: pxToRem(26),
            paddingBottom: pxToRem(10),
          }}
          className="hide-input-arrows"
          required={required}
          disabled={disabled}
        />
        {!disabled && (
          <Box alignSelf="center" margin={{ right: '18px' }}>
            <DateInput
              id={id}
              icon={<Calendar size="20px" color={isButtonFocused ? 'accent-1' : 'text'} />}
              value={effectiveValue}
              onChange={({ value }) => {
                setValue?.(dayjs(typeof value === 'string' ? value : value[0]).format('YYYY-MM-DD'));
                setIsCalendarOpen(false);
              }}
              buttonProps={
                {
                  open: isCalendarOpen,
                  onOpen: () => setIsCalendarOpen(true),
                  onClose: () => setIsCalendarOpen(false),
                } satisfies Pick<DropButtonProps, 'open' | 'onOpen' | 'onClose'> as ButtonType
              }
              calendarProps={{
                bounds: dateBounds,
                daysOfWeek: true,
                showAdjacentDays: 'trim',
                size: 'medium',
                header: ({ date, onPreviousMonth, onNextMonth, previousInBound, nextInBound }) => {
                  const current = dayjs(date);
                  const currentMonth = months[current.month()];
                  const currentYearValue = String(current.year());
                  const disabledMonths = months.filter((_, index) => isOutOfBounds(current.month(index), 'month'));

                  return (
                    <Box direction="row" pad={{ horizontal: 'small', vertical: 'xsmall' }}>
                      <Button
                        icon={<FormPrevious size="24px" />}
                        onClick={onPreviousMonth}
                        disabled={!previousInBound}
                        plain
                      />
                      <Box direction="row" gap="xsmall" align="center">
                        <Box width="200px">
                          <Select
                            options={months}
                            value={currentMonth}
                            disabled={disabledMonths}
                            onChange={({ option }) => setValue?.(lockToBounds(current.month(months.indexOf(option))))}
                            style={{
                              fontFamily: 'Lato, sans-serif',
                              fontWeight: 600,
                              fontSize: '18px',
                            }}
                          />
                        </Box>
                        <Box width="200px">
                          <Select
                            options={years}
                            value={currentYearValue}
                            disabled={disabledYears}
                            onChange={({ option }) => setValue?.(lockToBounds(current.year(parseInt(option))))}
                            style={{
                              fontFamily: 'Lato, sans-serif',
                              fontWeight: 600,
                              fontSize: '18px',
                            }}
                          />
                        </Box>
                      </Box>
                      <Button icon={<FormNext size="24px" />} onClick={onNextMonth} disabled={!nextInBound} plain />
                    </Box>
                  );
                },
                style: {
                  fontFamily: 'Lato, sans-serif',
                  paddingBottom: '6px',
                },
                fill: true,
              }}
              dropProps={{
                align: { top: 'bottom', right: 'right' },
              }}
              disabled={disabled}
              onFocus={() => setIsButtonFocused(true)}
              onBlur={() => setIsButtonFocused(false)}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderTextArea = () => (
    <TextArea
      id={id}
      name={name || id}
      placeholder={placeholder}
      minLength={minLength}
      maxLength={maxLength}
      rows={rows}
      value={typeof value === 'number' ? value?.toString() : value}
      onChange={(e) => setValue?.(e.target.value)}
      required={required}
      disabled={disabled}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      resize={false}
      fill
      plain
      style={{
        fontFamily: 'Lato, sans-serif',
        paddingTop: pxToRem(26),
        paddingBottom: pxToRem(10),
        minHeight: pxToRem(50),
        ...(opacity !== undefined ? { opacity } : {}),
        fieldSizing: 'content',
      }}
    />
  );

  const renderPasswordInput = () => (
    <TextInput
      id={id}
      name={name || id}
      focusIndicator={false}
      placeholder={placeholder}
      minLength={minLength || 10}
      maxLength={maxLength}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={(e) => setValue?.(e.target.value)}
      required={required}
      reverse
      disabled={disabled}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      plain
      style={{
        fontFamily: 'Lato, sans-serif',
        height: pxToRem(58),
        paddingTop: pxToRem(26),
        paddingBottom: pxToRem(10),
      }}
    />
  );

  const renderInputComponent = () => {
    switch (componentType) {
      case 'date':
        return renderDateInput();
      case 'percent':
        return renderPercentInput();
      case 'percentage':
        return renderPercentInput();
      case 'password':
        return renderPasswordInput();
      case 'textArea':
        return renderTextArea();
      default:
        return renderInput();
    }
  };

  const borderColor = error ? 'red' : isFocused ? 'accent-1' : 'light-5';
  //const buttonBorderColor = isButtonFocused ? 'accent-1' : 'transparent';

  return (
    <Box flex={componentType === 'textArea' ? 'grow' : undefined} {...boxProps}>
      <Box
        border={disabled ? undefined : { color: borderColor, size: 'small' }}
        background={disabled ? disabledBackground || 'light-6' : undefined}
        round="6px"
        height={componentType === 'textArea' ? { min: pxToRem(58) } : pxToRem(58)}
        flex={componentType === 'textArea' ? 'grow' : undefined}
        basis={componentType === 'textArea' ? 'auto' : undefined}
      >
        <Box round="6px" direction="row" fill="vertical">
          <Stack anchor={isActiveLabel ? 'top-left' : 'left'} guidingChild="last" fill>
            <Box pad={{ top: isActiveLabel ? 'xsmall' : undefined, left: 'small' }}>
              <Text size={isActiveLabel ? 'small' : 'medium'} color={isActiveLabel ? 'accent-1' : undefined}>
                {label}
                {required && !hideRequiredMarker && '*'}
              </Text>
            </Box>
            <Keyboard onEnter={onSubmit}>{renderInputComponent()}</Keyboard>
          </Stack>
          {componentType === 'password' && (
            <Button
              icon={
                showPassword ? (
                  <View size="medium" color={isButtonFocused ? 'brand' : undefined} />
                ) : (
                  <Hide size="medium" color={isButtonFocused ? 'brand' : undefined} />
                )
              }
              onClick={() => setShowPassword(!showPassword)}
              onFocus={() => setIsButtonFocused(true)}
              onBlur={() => setIsButtonFocused(false)}
              onMouseOver={() => setIsButtonFocused(true)}
              onMouseOut={() => setIsButtonFocused(false)}
            />
          )}
        </Box>
      </Box>
      {(error || fuseAdminOnly) && (
        <Box row gap="small" margin={{ top: '0.5rem', left: '0.75rem' }}>
          {error && (
            <Text color="red" size="0.75rem">
              {error}
            </Text>
          )}
          {error && fuseAdminOnly && <Line direction="vertical" margin="0" />}
          {fuseAdminOnly && (
            <Text color="dark-1" size="0.75rem">
              FuSE ADMINS ONLY
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
